import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";

const CookiesPage = () => {
  return (
    <div className="pt-32" data-testid="cookies-page">
      <SEO 
        title="Cookie Policy"
        description="Learn about the cookies OptimaGrowth uses on our website. Understand how we track visitors and how you can manage your cookie preferences."
        keywords="cookie policy, cookies, website tracking, privacy, OptimaGrowth cookies"
      />
      <section className="section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl font-bold text-optima-dark mb-6">
                Cookie Policy
              </h1>
              <p className="text-slate-600 mb-8">
                Last updated: January 2026
              </p>

              <div className="prose max-w-none">
                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  1. What Are Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  2. How We Use Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  OptimaGrowth uses cookies and similar technologies for the following purposes:
                </p>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.1 Essential Cookies
                </h3>
                <p className="text-slate-700 mb-4">
                  These cookies are necessary for the website to function properly. They enable core functionality such as:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Page navigation</li>
                  <li>Access to secure areas</li>
                  <li>Form submissions</li>
                  <li>Session management</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.2 Analytics Cookies
                </h3>
                <p className="text-slate-700 mb-4">
                  We use analytics cookies to understand how visitors interact with our website. This helps us improve user experience:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Google Analytics - tracks page views, user behavior, and traffic sources</li>
                  <li>Session recording tools - helps us understand user journey</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.3 Functional Cookies
                </h3>
                <p className="text-slate-700 mb-4">
                  These cookies enable enhanced functionality and personalization:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Remembering your preferences (e.g., currency selection)</li>
                  <li>Chat widget functionality</li>
                  <li>Form pre-filling</li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  2.4 Marketing Cookies
                </h3>
                <p className="text-slate-700 mb-4">
                  We may use marketing cookies to deliver relevant advertisements:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Meta Pixel - for Facebook/Instagram advertising</li>
                  <li>Google Ads - for remarketing and conversion tracking</li>
                  <li>LinkedIn Insight Tag - for B2B advertising</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  3. Third-Party Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  Some cookies on our website are set by third parties. We do not control these cookies. Third-party services we use include:
                </p>
                <table className="w-full border-collapse mb-4">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 pr-4 font-semibold text-optima-dark">Service</th>
                      <th className="text-left py-2 pr-4 font-semibold text-optima-dark">Purpose</th>
                      <th className="text-left py-2 font-semibold text-optima-dark">Privacy Policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 pr-4 text-slate-700">Google Analytics</td>
                      <td className="py-2 pr-4 text-slate-700">Website analytics</td>
                      <td className="py-2 text-optima-blue">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">View</a>
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 pr-4 text-slate-700">Meta (Facebook)</td>
                      <td className="py-2 pr-4 text-slate-700">Advertising & analytics</td>
                      <td className="py-2 text-optima-blue">
                        <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="hover:underline">View</a>
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 pr-4 text-slate-700">LinkedIn</td>
                      <td className="py-2 pr-4 text-slate-700">B2B advertising</td>
                      <td className="py-2 text-optima-blue">
                        <a href="https://www.linkedin.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:underline">View</a>
                      </td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-2 pr-4 text-slate-700">Calendly</td>
                      <td className="py-2 pr-4 text-slate-700">Booking widget</td>
                      <td className="py-2 text-optima-blue">
                        <a href="https://calendly.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">View</a>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  4. Managing Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  You can control and manage cookies in several ways:
                </p>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  4.1 Browser Settings
                </h3>
                <p className="text-slate-700 mb-4">
                  Most browsers allow you to refuse or accept cookies. Here's how to manage cookies in popular browsers:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Microsoft Edge</a></li>
                </ul>

                <h3 className="font-heading text-xl font-semibold text-optima-dark mt-6 mb-3">
                  4.2 Opt-Out Tools
                </h3>
                <p className="text-slate-700 mb-4">
                  You can opt out of specific tracking:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Google Analytics Opt-out</a></li>
                  <li><a href="https://www.facebook.com/help/568137493302217" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Facebook Ad Preferences</a></li>
                  <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-optima-blue hover:underline">Digital Advertising Alliance Opt-out</a></li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  5. Impact of Disabling Cookies
                </h2>
                <p className="text-slate-700 mb-4">
                  Please note that if you choose to disable cookies, some features of our website may not function properly. Essential functionality that may be affected includes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-slate-700">
                  <li>Currency preference storage</li>
                  <li>Form submissions</li>
                  <li>Session management</li>
                </ul>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  6. Updates to This Policy
                </h2>
                <p className="text-slate-700 mb-4">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. The updated policy will be posted on this page with a new "Last updated" date.
                </p>

                <h2 className="font-heading text-2xl font-bold text-optima-dark mt-8 mb-4">
                  7. Contact Us
                </h2>
                <p className="text-slate-700 mb-4">
                  If you have questions about our use of cookies, please contact us:
                </p>
                <p className="text-slate-700 mb-4">
                  <strong>OptimaGrowth</strong><br />
                  Email: info@optimagrowth.agency<br />
                  Phone: +27 66 122 9790<br />
                  Location: Cape Town, South Africa
                </p>

                <p className="text-slate-700 mb-4">
                  For more information about how we handle your data, please see our <Link to="/privacy" className="text-optima-blue hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiesPage;
